import type {
  EquipmentImportPreview,
  EquipmentImportWrite,
  EquipmentImportResult,
} from "./physicalImport"
import type {
  PhysicalKind,
  PhysicalFilters,
  PhysicalIdentity,
  PhysicalIdentityWrite,
  PhysicalInspection,
  PhysicalPage,
  PhysicalLink,
  PhysicalLinkWrite,
  PhysicalLinkEvent,
} from "../types/physical"
import { parseApiError } from "./regulatoryApi"
let session: string | null = null
let generation = 0
export function setPhysicalSession(key: string | null) {
  if (key !== session) {
    session = key
    generation++
  }
}
export const physicalPaths: Record<PhysicalKind, string> = {
  site: "physical-sites",
  asset: "physical-assets",
  component: "physical-components",
}
export function physicalQuery(filters: PhysicalFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== "") params.set(k, String(v))
  })
  return params.toString() ? `?${params}` : ""
}
async function request<T>(
  path: string,
  signal?: AbortSignal,
  method = "GET",
  body?: unknown,
): Promise<T> {
  const started = generation
  const response = await fetch(`/api/v2/${path}`, {
    method,
    credentials: "same-origin",
    cache: "no-store",
    signal,
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (started !== generation) throw new DOMException("La session a changé", "AbortError")
  if (!response.ok) {
    if (response.status === 401 && typeof window !== "undefined")
      window.dispatchEvent(new Event("cloud-session-expired"))
    throw parseApiError(await response.text(), response.status)
  }
  const value = (await response.json()) as T
  if (started !== generation) throw new DOMException("La session a changé", "AbortError")
  return value
}
const target = (id: string, equipment: boolean) =>
  `${equipment ? "equipment" : "inspections"}/${encodeURIComponent(id)}/physical-link`
export const physicalApi = {
  importPreview: (id: string, signal?: AbortSignal) =>
    request<EquipmentImportPreview>(
      `physical-sites/${encodeURIComponent(id)}/equipment-import/preview`,
      signal,
    ),
  importEquipment: (id: string, body: EquipmentImportWrite) =>
    request<EquipmentImportResult>(
      `physical-sites/${encodeURIComponent(id)}/equipment-import`,
      undefined,
      "POST",
      body,
    ),
  list: (kind: PhysicalKind, filters?: PhysicalFilters, signal?: AbortSignal) =>
    request<PhysicalPage<PhysicalIdentity>>(physicalPaths[kind] + physicalQuery(filters), signal),
  get: (kind: PhysicalKind, id: string, signal?: AbortSignal) =>
    request<PhysicalIdentity>(`${physicalPaths[kind]}/${encodeURIComponent(id)}`, signal),
  save: (kind: PhysicalKind, body: PhysicalIdentityWrite, id?: string) =>
    request<PhysicalIdentity>(
      `${physicalPaths[kind]}${id ? `/${encodeURIComponent(id)}` : ""}`,
      undefined,
      id ? "PUT" : "POST",
      body,
    ),
  inspections: (kind: PhysicalKind, id: string, filters?: PhysicalFilters, signal?: AbortSignal) =>
    request<PhysicalPage<PhysicalInspection>>(
      `${physicalPaths[kind]}/${encodeURIComponent(id)}/inspections${physicalQuery(filters)}`,
      signal,
    ),
  link: (id: string, equipment: boolean, signal?: AbortSignal) =>
    request<PhysicalLink>(target(id, equipment), signal),
  saveLink: (id: string, equipment: boolean, body: PhysicalLinkWrite) =>
    request<PhysicalLink>(target(id, equipment), undefined, "PUT", body),
  history: (id: string, equipment: boolean, filters?: PhysicalFilters, signal?: AbortSignal) =>
    request<PhysicalPage<PhysicalLinkEvent>>(
      `${target(id, equipment)}/history${physicalQuery(filters)}`,
      signal,
    ),
}
export function identityWrite(value: PhysicalIdentity): PhysicalIdentityWrite {
  const base = {
    name: value.name,
    code: value.code,
    notes: value.notes,
    archived: value.archived,
    expectedVersion: value.version,
  }
  switch (value.entityType) {
    case "site":
      return { ...base, address: value.address ?? "", customerUuid: value.customerUuid ?? null }
    case "asset":
      return {
        ...base,
        physicalSiteUuid: value.physicalSiteUuid,
        manufacturer: value.manufacturer ?? "",
        model: value.model ?? "",
        serialNumber: value.serialNumber ?? "",
      }
    case "component":
      return { ...base, physicalAssetUuid: value.physicalAssetUuid }
  }
}
export function equipmentLinkWrite(
  link: PhysicalLink,
  asset: string | null,
  components: Record<string, string | null>,
  reason: string,
): PhysicalLinkWrite {
  return {
    expectedVersion: link.version,
    physicalAssetUuid: asset,
    reason: reason.trim(),
    components: link.components.map((c) => ({
      contextUuid: c.contextUuid,
      physicalComponentUuid: asset ? components[c.contextUuid] ?? null : null,
    })),
  }
}
export function physicalError(reason: unknown): string {
  const e = reason as { status?: number; message?: string }
  if (e?.status === 409)
    return "Les données ont changé ou cette identité est déjà utilisée. Votre saisie est conservée. Rechargez la version actuelle avant de réessayer."
  if (e?.status === 404)
    return "Cet élément est indisponible ou n’est pas accessible dans votre entreprise."
  if (e?.status === 422 && /Unlink physical assets/i.test(e.message ?? ""))
    return "Dissociez d’abord les équipements de cette inspection, puis changez son site."
  if (e?.status === 422 && /Unlink physical component/i.test(e.message ?? ""))
    return "Dissociez d’abord le composant physique avant de supprimer ce contexte d’inspection."
  return e?.message || "Le serveur est indisponible. Réessayez sans fermer ce formulaire."
}
